import { PeopleSdk } from 'lib-klab-sdks';

type PagedEmployeeHierarchyResponse = PeopleSdk.PagedEmployeeHierarchyResponse;
type EmployeeHierarchyResponse = PeopleSdk.EmployeeHierarchyResponse;
type PagedEmployeeByManagerResponse = PeopleSdk.PagedEmployeeByManagerResponse;
type EmployeeByManagerItem = PeopleSdk.EmployeeByManagerItem;
type EmployeeHierarchyCreateRequest = PeopleSdk.EmployeeHierarchyCreateRequest;
type EmployeeHierarchyUpdateRequest = PeopleSdk.EmployeeHierarchyUpdateRequest;

function buildPage(number: number, size: number, totalElements: number): PeopleSdk.PaginationResponse {
    return {
        number,
        size,
        totalElements,
        totalPages: Math.max(1, Math.ceil(totalElements / size)),
    };
}

function deepClone<T>(v: T): T {
    return JSON.parse(JSON.stringify(v));
}

const loc = (id: number, name: string): PeopleSdk.LocationObject => ({ locationId: id, name });
const dept = (id: number, name: string): PeopleSdk.DepartmentObject => ({ departmentId: id, name });
const role = (id: number, name: string): PeopleSdk.RoleObject => ({ roleId: id, name });

const defaultHierarchies: EmployeeHierarchyResponse[] = [
    {
        employeeHierarchyId: 1,
        employeeId: 1,
        managerId: 0,
        code: 'EMP001',
        name: 'Juan',
        lastname: 'Pérez',
        location: loc(1, 'Lima HQ'),
        department: dept(1, 'RRHH'),
        role: role(1, 'Líder'),
        assignedEmployeesCount: 2,
        createdAt: '2024-01-01T00:00:00',
        updatedAt: '2024-01-01T00:00:00',
    } as EmployeeHierarchyResponse,
    {
        employeeHierarchyId: 2,
        employeeId: 3,
        managerId: 0,
        code: 'EMP003',
        name: 'Carlos',
        lastname: 'López',
        location: loc(2, 'Arequipa'),
        department: dept(2, 'Operaciones'),
        role: role(1, 'Líder'),
        assignedEmployeesCount: 1,
        createdAt: '2024-01-01T00:00:00',
        updatedAt: '2024-01-01T00:00:00',
    } as EmployeeHierarchyResponse,
];

const defaultByManager: Record<number, EmployeeByManagerItem[]> = {
    1: [
        { employeeHierarchyId: 101, code: 'EMP002', name: 'María', lastname: 'García', location: loc(1, 'Lima HQ'), department: dept(1, 'RRHH'), role: role(2, 'Colaborador') },
        { employeeHierarchyId: 102, code: 'EMP004', name: 'Ana', lastname: 'Martínez', location: loc(1, 'Lima HQ'), department: dept(3, 'Finanzas'), role: role(2, 'Colaborador') },
    ],
    3: [
        { employeeHierarchyId: 103, code: 'EMP005', name: 'Luis', lastname: 'Rodríguez', location: loc(2, 'Arequipa'), department: dept(2, 'Operaciones'), role: role(2, 'Colaborador') },
    ],
};

class EmployeeHierarchiesSdkMockStore {
    private hierarchies: EmployeeHierarchyResponse[] = deepClone(defaultHierarchies);
    private byManager: Record<number, EmployeeByManagerItem[]> = deepClone(defaultByManager);
    private nextHierarchyId = 100;

    getAllEmployeeHierarchies(
        page = 0,
        size = 10,
        _locationId?: number,
        _departmentId?: number,
        _roleId?: number,
    ): PagedEmployeeHierarchyResponse {
        const p = Math.max(0, page);
        const s = Math.max(1, size);
        const total = this.hierarchies.length;
        const start = p * s;
        const content = this.hierarchies.slice(start, start + s);
        return {
            content: deepClone(content),
            page: buildPage(p, s, total),
        };
    }

    getEmployeesByManagerId(
        managerId: number,
        page?: number,
        size?: number,
        locationId?: number,
        departmentId?: number,
        roleId?: number,
    ): PagedEmployeeByManagerResponse {
        const list = (this.byManager[managerId] ?? []).filter((item) => {
            const matchesLocation = locationId == null || item.location?.locationId === locationId;
            const matchesDepartment = departmentId == null || item.department?.departmentId === departmentId;
            const matchesRole = roleId == null || item.role?.roleId === roleId;
            return matchesLocation && matchesDepartment && matchesRole;
        });
        const total = list.length;
        if (page === undefined && size === undefined) {
            return {
                content: deepClone(list),
                page: buildPage(0, Math.max(1, total), total),
            };
        }
        const p = Math.max(0, page ?? 0);
        const s = Math.max(1, size ?? 10);
        const start = p * s;
        const content = list.slice(start, start + s);
        return {
            content: deepClone(content),
            page: buildPage(p, s, total),
        };
    }

    createEmployeeHierarchy(payload: EmployeeHierarchyCreateRequest): void {
        const employeeId = Number(payload.employeeId);
        const managerId = Number(payload.managerId ?? 0);
        const employeeHierarchyId = this.nextHierarchyId++;
        const newItem: EmployeeByManagerItem = {
            employeeHierarchyId,
            code: `EMP${employeeId}`,
            name: 'Empleado',
            lastname: `#${employeeId}`,
            location: loc(1, 'Lima HQ'),
            department: dept(1, 'RRHH'),
            role: role(2, 'Colaborador'),
        };
        if (!this.byManager[managerId]) this.byManager[managerId] = [];
        this.byManager[managerId].push(newItem);
        this.hierarchies.push({
            employeeHierarchyId,
            employeeId,
            managerId,
            code: newItem.code,
            name: newItem.name,
            lastname: newItem.lastname,
            location: newItem.location,
            department: newItem.department,
            role: newItem.role,
            assignedEmployeesCount: 0,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        } as EmployeeHierarchyResponse);
    }

    updateEmployeeHierarchy(employeeHierarchyId: number, payload: EmployeeHierarchyUpdateRequest): void {
        const managerId = Number(payload.managerId ?? 0);
        const employeeId = Number(payload.employeeId);
        const replacement: EmployeeByManagerItem = {
            employeeHierarchyId,
            code: `EMP${employeeId}`,
            name: 'Empleado',
            lastname: `#${employeeId}`,
            location: loc(1, 'Lima HQ'),
            department: dept(1, 'RRHH'),
            role: role(2, 'Colaborador'),
        };

        Object.keys(this.byManager).forEach((key) => {
            const id = Number(key);
            this.byManager[id] = (this.byManager[id] ?? [])
                .filter((item) => item.employeeHierarchyId !== employeeHierarchyId);
        });
        if (!this.byManager[managerId]) this.byManager[managerId] = [];
        this.byManager[managerId].push(replacement);
    }

    deleteEmployeeHierarchyById(employeeHierarchyId: number): void {
        this.hierarchies = this.hierarchies.filter((h) => h.employeeHierarchyId !== employeeHierarchyId);
    }
}

const store = new EmployeeHierarchiesSdkMockStore();

export const employeeHierarchiesSdkMock = {
    getAllEmployeeHierarchies: (
        page?: number,
        size?: number,
        locationId?: number,
        departmentId?: number,
        roleId?: number,
    ) => store.getAllEmployeeHierarchies(page ?? 0, size ?? 10, locationId, departmentId, roleId),
    getEmployeesByManagerId: (
        managerId: number,
        page?: number,
        size?: number,
        locationId?: number,
        departmentId?: number,
        roleId?: number,
    ) => store.getEmployeesByManagerId(managerId, page, size, locationId, departmentId, roleId),
    createEmployeeHierarchy: (payload: EmployeeHierarchyCreateRequest) => store.createEmployeeHierarchy(payload),
    updateEmployeeHierarchy: (employeeHierarchyId: number, payload: EmployeeHierarchyUpdateRequest) =>
        store.updateEmployeeHierarchy(employeeHierarchyId, payload),
    deleteEmployeeHierarchyById: (employeeHierarchyId: number) => store.deleteEmployeeHierarchyById(employeeHierarchyId),
};
